import { memo, useMemo } from 'react';

import { Box, Checkbox } from '@mui/material';
import { GridColDef, GridRowId } from '@mui/x-data-grid-pro';
import { useFormikContext } from 'formik';

import { ClusterAutoComplete } from 'components/autocompletes';
import { StoreOptionsProps } from 'components/autocompletes/StoreAutoComplete';
import { CissNumberField } from 'components/fields';
import { PanelGrid } from 'components/grid';
import { Cluster } from 'pages/complementar/cluster/types';
import { ConfigPedidoRemoveBtn } from 'pages/compra/configuracao-pedido/components';
import { useConfigPedidoListContext } from 'pages/compra/configuracao-pedido/contexts';
import { ClusterConfigPedido, ConfigPedidoProduto, ConfigPedidoProdutos } from 'pages/compra/configuracao-pedido/types';

type ConfigPedidoProdutosGrid = {
    loja: StoreOptionsProps | null;
    onRemoveProduto: (ids: Set<GridRowId>) => void;
};

/**
 * Transforma os produtos em linhas para o grid.
 */
const getRows = (products: ConfigPedidoProduto[]) =>
    products.map((item, index) => ({
        id: item.produto.idProduto,
        index,
        idProduto: item.produto.idProduto,
        cdProduto: item.produto.cdProduto || '',
        dsProduto: item.produto.dsProduto,
        fgFracionavel: item.produto.fgFracionavel ?? item.produto?.unidadeMedida?.fgFracionavel ?? false,
        dsCategoria: item.produto.dsGrupo || '',
        dsMarca: item.produto.dsMarca,
        qtMinimaProduto: item.qtMinimaProduto,
        qtMaximaProduto: item.qtMaximaProduto,
        peOferta: item.peOferta,
        fgShowPeOferta: item.fgShowPeOferta,
        configPedidoProdutoClusterLoja: item.configPedidoProdutoClusterLoja,
        fgExibirSomenteParaClusterLoja: item.fgExibirSomenteParaClusterLoja,
    }));

type GridRows = ReturnType<typeof getRows>;

/**
 * Filtra os produtos com base no valor do filtro.
 */
const getFilteredRows = (products: GridRows, valueFilter: string) =>
    products.filter((item) => {
        return (
            !valueFilter ||
            item.dsProduto.toLowerCase().includes(valueFilter.trim().toLowerCase()) ||
            item.dsCategoria.toLowerCase().includes(valueFilter.trim().toLowerCase()) ||
            item.cdProduto.toString().includes(valueFilter.trim().toLowerCase())
        );
    });

const CELL_MIN_HEIGHT = 52;

export const ConfigPedidoProdutosGrid = memo(({ loja, onRemoveProduto }: ConfigPedidoProdutosGrid) => {
    const { valueFilter, setSelectedRows } = useConfigPedidoListContext();
    const { values, setFieldValue } = useFormikContext<ConfigPedidoProdutos>();

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: 'cdProduto',
                headerName: 'Código',
                type: 'number',
                width: 94,
                renderCell: ({ row }) => (
                    <span style={{ height: CELL_MIN_HEIGHT, display: 'inline-flex', alignItems: 'center', alignSelf: 'flex-start' }}>
                        {row.cdProduto}
                    </span>
                ),
            },
            {
                field: 'dsProduto',
                headerName: 'Descrição',
                type: 'string',
                flex: 1,
                minWidth: 100,
                renderCell: ({ row }) => (
                    <span style={{ height: CELL_MIN_HEIGHT, display: 'inline-flex', alignItems: 'center', alignSelf: 'flex-start' }}>
                        {row.dsProduto}
                    </span>
                ),
            },
            {
                field: 'dsMarca',
                headerName: 'Marca',
                type: 'string',
                width: 70,
                renderCell: ({ row }) => (
                    <span style={{ height: CELL_MIN_HEIGHT, display: 'inline-flex', alignItems: 'center', alignSelf: 'flex-start' }}>
                        {row.dsMarca}
                    </span>
                ),
            },
            {
                field: 'dsCategoria',
                headerName: 'Categoria',
                type: 'string',
                width: 94,
                renderCell: ({ row }) => (
                    <span style={{ height: CELL_MIN_HEIGHT, display: 'inline-flex', alignItems: 'center', alignSelf: 'flex-start' }}>
                        {row.dsCategoria}
                    </span>
                ),
            },
            {
                field: 'qtMinimaProduto',
                headerName: 'Quantidade mínima',
                width: 84,
                headerAlign: 'center',
                renderCell: ({ row }) => (
                    <div style={{ height: CELL_MIN_HEIGHT, display: 'flex', alignItems: 'center', alignSelf: 'flex-start' }}>
                        <CissNumberField
                            size="small"
                            inputType={row.fgFracionavel ? 'decimal' : 'number'}
                            allowNegative={false}
                            value={row.qtMinimaProduto}
                            disabled={Boolean(loja)}
                            onChange={(e) => setFieldValue(`configPedidoProdutos.${row.index}.qtMinimaProduto`, Number(e.target.value))}
                        />
                    </div>
                ),
            },
            {
                field: 'qtMaximaProduto',
                headerName: 'Quantidade máxima',
                width: 84,
                headerAlign: 'center',
                renderCell: ({ row }) => (
                    <div style={{ height: CELL_MIN_HEIGHT, display: 'flex', alignItems: 'center', alignSelf: 'flex-start' }}>
                        <CissNumberField
                            size="small"
                            inputType={row.fgFracionavel ? 'decimal' : 'number'}
                            allowNegative={false}
                            value={row.qtMaximaProduto}
                            disabled={Boolean(loja)}
                            onChange={(e) => setFieldValue(`configPedidoProdutos.${row.index}.qtMaximaProduto`, Number(e.target.value))}
                        />
                    </div>
                ),
            },
            {
                field: 'peOferta',
                headerName: 'Desconto (%)',
                width: 94,
                headerAlign: 'center',
                renderCell: ({ row }) => (
                    <div style={{ height: CELL_MIN_HEIGHT, display: 'flex', alignItems: 'center', alignSelf: 'flex-start' }}>
                        <CissNumberField
                            size="small"
                            inputType="decimal"
                            maxValue={100}
                            forceMaxValue
                            allowNegative={false}
                            value={row.peOferta || 0}
                            onChange={(e) => setFieldValue(`configPedidoProdutos.${row.index}.peOferta`, Number(e.target.value))}
                        />
                    </div>
                ),
            },
            {
                field: 'fgShowPeOferta',
                align: 'center',
                headerName: 'Exibir desconto',
                width: 70,
                headerAlign: 'center',
                renderCell: ({ row }) => (
                    <div style={{ height: CELL_MIN_HEIGHT, display: 'flex', alignItems: 'center', alignSelf: 'flex-start' }}>
                        <Checkbox
                            disabled={Boolean(loja)}
                            checked={row.fgShowPeOferta}
                            onChange={(e, checked) => setFieldValue(`configPedidoProdutos.${row.index}.fgShowPeOferta`, checked)}
                        />
                    </div>
                ),
            },
            {
                field: 'configPedidoProdutoClusterLoja',
                headerName: 'Clusters',
                flex: 1,
                minWidth: 240,
                headerAlign: 'center',
                renderCell: ({ row }) => (
                    <ClusterAutoComplete
                        multiple
                        label=""
                        size="small"
                        sx={{ flex: 1, marginY: '6px' }}
                        disabled={Boolean(loja)}
                        value={row.configPedidoProdutoClusterLoja?.map((item: ClusterConfigPedido) => item.clusterLoja) || []}
                        onChange={(e, value) => {
                            setFieldValue(
                                `configPedidoProdutos.${row.index}.configPedidoProdutoClusterLoja`,
                                value.map((cluster: Cluster) => ({
                                    clusterLoja: cluster,
                                    fgAtivo: true,
                                    idClusterLoja: cluster.idClusterLoja,
                                    idConfigPedidoProduto: row.id,
                                })),
                            );
                        }}
                    />
                ),
            },
            {
                field: 'fgExibirSomenteParaClusterLoja',
                align: 'center',
                headerName: 'Exibir somente para clusters',
                width: 96,
                headerAlign: 'center',
                renderCell: ({ row }) => (
                    <div style={{ height: CELL_MIN_HEIGHT, display: 'flex', alignItems: 'center', alignSelf: 'flex-start' }}>
                        <Checkbox
                            disabled={Boolean(loja)}
                            checked={row.fgExibirSomenteParaClusterLoja}
                            onChange={(e, checked) => setFieldValue(`configPedidoProdutos.${row.index}.fgExibirSomenteParaClusterLoja`, checked)}
                        />
                    </div>
                ),
            },
            {
                field: 'acoes',
                align: 'center',
                headerAlign: 'center',
                headerName: 'Ações',
                filterable: false,
                sortable: false,
                hideable: false,
                disableColumnMenu: true,
                width: 70,
                renderCell: ({ row }) => (
                    <div style={{ height: CELL_MIN_HEIGHT, display: 'flex', alignItems: 'center', alignSelf: 'flex-start' }}>
                        <ConfigPedidoRemoveBtn disabled={Boolean(loja)} onClick={() => onRemoveProduto(new Set([row.idProduto]))} />
                    </div>
                ),
            },
        ],
        [loja, values.configPedidoProdutos],
    );

    const rows = useMemo(() => {
        const rows = getRows(values.configPedidoProdutos);

        return getFilteredRows(rows, valueFilter);
    }, [values.configPedidoProdutos, valueFilter]);

    return (
        // comportamento para deixar checkbox de seleção da linha alinhado ao início mesmo se a altura da linha alterar.
        <Box sx={{ '& .MuiDataGrid-cellCheckbox': { alignItems: 'flex-start', paddingTop: `${(CELL_MIN_HEIGHT - 42) / 2}px` } }}>
            <PanelGrid
                rowCellHeight="auto"
                checkboxSelection
                rows={rows}
                columns={columns}
                reload={false}
                remote={false}
                loading={false}
                onClickRow={setSelectedRows}
            />
        </Box>
    );
});
